import { Component, ComponentType, forwardRef, ReactElement, ReactNode, useCallback, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import { Dialog, dialogClasses } from '@mui/material';

interface TransitionProps {
    in: boolean;
    children: ReactNode;
}

const SlideIn = forwardRef(function SlideIn(
    {
        in: inProp,
        children,
    }: TransitionProps & {
        children: ReactElement<any, any>;
    },
    ref: AnyFunction
) {
    // Create ref for CSSTransition
    const nodeRef = useRef<HTMLSpanElement>(null);

    const combinedRef = useCallback(
        (r) => {
            ref(r);

            nodeRef.current = r;
        },
        [ref]
    );

    return (
        <CSSTransition
            nodeRef={nodeRef}
            in={inProp}
            classNames="animated-modal"
            timeout={200}
            appear
            mountOnEnter
            unmountOnExit
        >
            <span ref={combinedRef} className="animated-modal-content" tabIndex={-1}>
                {children}
            </span>
        </CSSTransition>
    );
});

export interface AnimatedModalProps {
    open: boolean;
    onClose: () => any;
    narrow?: boolean;
    disablePortal?: boolean;
    container?: HTMLElement;
}

export const AnimationDuration = 200;

export default function animatedModal<P>(
    WrappedComponent: ComponentType<P>,
    { className = '', narrow = false, disableBackdropClick = false } = {}
): ComponentType<P & AnimatedModalProps> {
    return class WrappedModal extends Component<P & AnimatedModalProps, { props: P; open: boolean }> {
        update: any;

        constructor(props: P & AnimatedModalProps) {
            super(props);

            this.state = { props, open: props.open };
        }

        static getDerivedStateFromProps(props: P & AnimatedModalProps, prevState: any) {
            // If the modal is closing, state update will be handled by componentDidUpdate on a delay
            if (prevState.open && !props.open) {
                return { open: props.open };
            }

            return {
                open: props.open,
                props,
            };
        }

        override componentDidUpdate(prevProps: P & AnimatedModalProps) {
            const { open } = this.props;
            if (prevProps.open && !open) {
                if (this.update) {
                    clearTimeout(this.update);
                }

                this.update = setTimeout(() => {
                    this.setState({ props: this.props });
                }, AnimationDuration);
            }
        }

        override componentWillUnmount() {
            clearTimeout(this.update);
        }

        override render() {
            const { open = false, onClose, narrow: narrowProp, disablePortal, container } = this.props;
            const { props } = this.state;

            const _onClose = (evt, reason) => {
                if (disableBackdropClick && reason === 'backdropClick') {
                    return;
                }

                onClose();
            };

            return (
                <Dialog
                    open={open}
                    maxWidth={narrow && narrowProp !== false ? 'xs' : 'md'}
                    onClose={_onClose}
                    scroll="body"
                    container={container}
                    disablePortal={disablePortal}
                    slots={{
                        transition: SlideIn,
                    }}
                    sx={{
                        zIndex: 900,
                        ...(APP
                            ? {
                                  marginTop: '70px',
                                  marginBottom: '70px',
                              }
                            : {}),
                        '@media (max-height: 600px)': {
                            marginTop: 0,
                            marginBottom: 0,
                        },
                    }}
                    slotProps={{
                        paper: {
                            sx: (theme) => ({
                                [`&.${dialogClasses.paperScrollBody}`]: {
                                    [theme.breakpoints.down('sm')]: {
                                        maxWidth: '100%',
                                        margin: '32px 0',
                                    },
                                    '@media (max-height: 600px)': {
                                        marginTop: '8px',
                                        marginBottom: '8px',
                                    },
                                    width: '100%',
                                },
                            }),
                        },
                    }}
                    className={`animated-modal ${className}`}
                    closeAfterTransition
                >
                    <span>
                        <WrappedComponent {...props} />
                    </span>
                </Dialog>
            );
        }
    };
}
